/**
 * QA Panel — Preset controller.
 *
 * Lets QA export the current panel state as a JSON file and import it later,
 * preserving edits across session expiry. Also supports auto-saving to
 * localStorage so edits survive session loss transparently.
 *
 * The controller works with the existing BackofficeController and
 * JackpotMockController — it reads their state to build a snapshot and
 * calls their mutation methods to apply one.
 */

import { useAppStore } from '../../../store/app/appStore';
import { useFeatureFlagsStore } from '../../../store/featureFlags/featureFlagsStore';
import { useCampaignsStore, isTournamentSource, isRebateSource, TOURNAMENTS_CAMPAIGN_TYPE, REBATE_CAMPAIGN_TYPE } from '../../campaigns';
import type { ITournament, IParsedTournament, IPrizePopCampaign, IRebateCampaign } from '../../campaigns';
import type { BrandingType } from '../../../types/api/api.types';
import type IGameSettings from '../../../types/models/IGameSettings';
import type ISectionSettings from '../../../types/models/ISectionSettings';
import { useJackpotsStore } from '../../jackpots';
import type { BackofficeController } from '../backoffice/backofficeController';
import type { CampaignsController } from '../campaigns/campaignsController';
import type { PrizePopController } from '../campaigns/prizePopController';
import { buildDefaultCashbackLevels } from '../campaigns/rebateController';
import type { RebateController } from '../campaigns/rebateController';
import type { JackpotMockController, JackpotWinsMockEntry } from '../jackpot/jackpotMockController';

/* ── Preset data shape ─────────────────────────────────────────── */

export interface QaPresetGameOverride {
    topGame?: boolean;
    isInTournament?: boolean;
    gameFeatures?: string[];
    jackpotName?: string | null;
    displayJackpotName?: string | null;
    activatedOn?: string | null;
    addedToLobbyOn?: string | null;
    currentPlayers?: number;
    minBet?: number;
    maxBet?: number;
}

export interface QaPresetSectionLayout {
    name: string;
    gameKeys: string[];
}

export interface QaPreset {
    version: 1;
    timestamp: string;
    /** Section structure: names in order, each with its game keys in order.
     *  On import, sections are reordered/pruned/rebuilt to match this layout. */
    sectionLayout: QaPresetSectionLayout[];
    gameOverrides: Record<string, QaPresetGameOverride>;
    betCurrency: string;
    casino: {
        openType: 'fullscreen' | 'ingame';
        seasonalBranding: string | null;
        noJackpot: boolean;
        /** Optional — older presets may omit this field. Empty string means
         *  "no currency was loaded at export time" and is treated like absent. */
        currency?: string;
    };
    jackpotOverrides: Array<{
        jackpotName: string;
        levelId: number;
        value: number;
    }>;
    /**
     * Synthetic InstantLink injection (Jackpot Mock tab). Optional — older
     * presets omit it, and it's only written when QA actually injected the
     * instance this session (a live production InstantLink is never
     * exported). On import the value is re-injected, which auto-enables
     * jackpot mock mode.
     */
    instantJackpotMock?: {
        value: number;
    };
    /**
     * Statistics-drawer win mocking (Jackpot Mock tab → "Statistics mock"). Optional — older
     * presets omit it and are left untouched on import.
     *
     * Stored as GENERATION PARAMETERS, not as the generated rows: the factory is deterministic per
     * jackpot name, so replaying the parameters reproduces the same data while keeping the JSON
     * small. Re-generating on import auto-enables jackpot mock mode, so it waits for jackpot data
     * exactly like `jackpotOverrides` does.
     */
    jackpotWinsMock?: {
        mockMode: boolean;
        entries: JackpotWinsMockEntry[];
    };
    /**
     * Campaigns tab state. The Tournaments, PrizePop, and Rebate sub-tabs each
     * have their own `mockMode` flag, so the preset carries all three
     * independently:
     *   - `mockMode` mirrors `campaignsStore.mockMode` (tournaments sub-tab)
     *   - `prizePopMockMode` mirrors `campaignsStore.mockMode` (PrizePop sub-tab)
     *   - `rebateMockMode` mirrors `campaignsStore.mockMode` (Rebate sub-tab)
     *
     * The asymmetric naming preserves back-compat with presets written before
     * the PrizePop sub-tab existed (where `mockMode` always referred to
     * tournaments). The same back-compat principle applies to `rebateMockMode`
     * — older presets omit it and fall through untouched.
     *
     * Real campaigns/tournaments fetched via the paste-creds flow are NOT
     * persisted — they reload from the next poll once mocking is off, or
     * from a fresh paste-fetch.
     *
     * Mock tournaments are stored in their RAW `ITournament` shape (`games`
     * as `string[]`) because parsed Maps don't survive `JSON.stringify`.
     * Mock PrizePop and Rebate campaigns natively use `string[]` for `games`,
     * so no stripping step is needed.
     *
     * The synthetic `scoreBoard`, `score`, generated `prizes`, and the raw
     * `prizePlaces` (optional — older presets omit it or carry it as
     * `playerCount`) ride along on the mock `ITournament` object, so the QA
     * "Active players" and "Prize places" fields round-trip through this same
     * path with no extra import logic — the re-injected tournament reproduces
     * both the Ranking and Rewards lists exactly.
     */
    campaigns?: {
        mockMode: boolean;
        mockTournaments: Array<ITournament & { prizePlaces?: number }>;
        prizePopMockMode?: boolean;
        mockPrizePopCampaigns?: IPrizePopCampaign[];
        rebateMockMode?: boolean;
        mockRebateCampaigns?: IRebateCampaign[];
    };
    /** QA panel popup theme — 'dark' or 'light'. */
    theme: string;
}

/* ── State exposed to the popup ────────────────────────────────── */

export interface PresetState {
    autoSaveEnabled: boolean;
    hasStoredPreset: boolean;
    lastSavedAt: string | null;
}

/* ── Import result ─────────────────────────────────────────────── */

export interface PresetImportResult {
    success: boolean;
    applied: number;
    skipped: number;
    error?: string;
}

/* ── localStorage keys ─────────────────────────────────────────── */

const LS_AUTO_SAVE_KEY = 'qa-panel-autosave-enabled';
const LS_PRESET_KEY = 'qa-panel-preset';

/* ── Helpers ───────────────────────────────────────────────────── */

function safeGetItem(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeSetItem(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // localStorage may be unavailable in sandboxed contexts
    }
}

function safeRemoveItem(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // localStorage may be unavailable in sandboxed contexts
    }
}

/**
 * Convert a tournament (raw or parsed) to a JSON-portable `ITournament`
 * with `games: string[]`. Parsed tournaments carry `IGameSettings[]` (with
 * Map values inside) which would either lose data via JSON.stringify or
 * require a custom replacer. We strip them back to game-key strings — the
 * same shape the live API delivers — and let `addMockTournament` re-parse
 * on import.
 *
 * The parsed tournament's `gamesKeys` is a `{ [gameKey]: name }` map; its
 * keys are the canonical game-key list. When unavailable (e.g. an older
 * preset, or a tournament added before supportedGames was populated and
 * thus not parsed), we read each `IGameSettings.gameKey` directly. If
 * `games` is already `string[]`, we copy it through.
 */
function stripParsedGames(tournament: ITournament | IParsedTournament): ITournament {
    const parsed = tournament as Partial<IParsedTournament> & ITournament;
    let gameKeys: string[];

    if (parsed.gamesKeys && typeof parsed.gamesKeys === 'object') {
        gameKeys = Object.keys(parsed.gamesKeys);
    } else if (Array.isArray(tournament.games) && tournament.games.length > 0) {
        const first = tournament.games[0];
        if (typeof first === 'string') {
            gameKeys = tournament.games as string[];
        } else {
            gameKeys = (tournament.games as IGameSettings[]).map((g) => g.gameKey);
        }
    } else {
        gameKeys = [];
    }

    // Spread the runtime tournament object so any extra fields the controller
    // attached at construction time (formula, minSpinNumber, minStake,
    // formulaParams, visibleEndDate) survive the round-trip. The store's
    // parser preserves them via the spread inside `addMockTournament`.
    const { gamesKeys: _gamesKeys, ...rest } = parsed;
    void _gamesKeys;
    return {
        ...(rest as ITournament),
        games: gameKeys,
    };
}

/* ── Controller ────────────────────────────────────────────────── */

const AUTO_SAVE_DEBOUNCE_MS = 800;

export class PresetController {
    private backoffice: BackofficeController;
    private jackpotMock: JackpotMockController;
    private campaigns: CampaignsController;
    private prizePop: PrizePopController;
    private rebate: RebateController;
    private onChange: (() => void) | null = null;
    private autoSaveEnabled = false;
    private lastSavedAt: string | null = null;
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private jackpotStoreUnsubscribe: (() => void) | null = null;
    private instantMockUnsubscribe: (() => void) | null = null;
    private winsMockUnsubscribe: (() => void) | null = null;

    constructor(
        backoffice: BackofficeController,
        jackpotMock: JackpotMockController,
        campaigns: CampaignsController,
        prizePop: PrizePopController,
        rebate: RebateController,
    ) {
        this.backoffice = backoffice;
        this.jackpotMock = jackpotMock;
        this.campaigns = campaigns;
        this.prizePop = prizePop;
        this.rebate = rebate;
    }

    private storeUnsubscribe: (() => void) | null = null;
    private pendingRestore = false;

    /**
     * Wire up listeners and restore auto-save state from localStorage.
     * The onChange callback is called whenever the preset state changes,
     * so the popup can re-render.
     */
    init(onChange: () => void): void {
        this.onChange = onChange;

        // Restore auto-save setting from localStorage.
        const saved = safeGetItem(LS_AUTO_SAVE_KEY);
        if (saved === '1') {
            this.autoSaveEnabled = true;
            // Defer restore until sections are loaded — on a fresh page load
            // the settings fetch hasn't completed yet, so sections is empty
            // and every game override would be skipped.
            this.deferredRestore();
        }
    }

    /**
     * Apply the stored preset once the lobby is fully initialized.
     * Waits for `settingsRequestDone === true` (set AFTER sections,
     * supportedGames, translations, and feature flags are all loaded),
     * then adds a 1-second delay so async init operations that run
     * after the flag (fetchCurrentPlayers, jackpot polling first tick,
     * favorites/tournament setTimeout callbacks) have time to finish.
     * Without this delay, the preset's currentPlayers and jackpot
     * values get overwritten by the init results.
     */
    private deferredRestore(): void {
        const storedPreset = safeGetItem(LS_PRESET_KEY);
        if (!storedPreset) return;

        let parsed: unknown;
        try {
            parsed = JSON.parse(storedPreset);
            if (!this.isValidPreset(parsed)) return;
        } catch {
            // eslint-disable-next-line no-console
            console.warn('[QA Panel] Stored preset is corrupted — ignoring.');
            return;
        }

        const validPreset = parsed;

        const applyAfterDelay = () => {
            // Wait an extra second for async init operations to settle.
            setTimeout(() => {
                this.applyPreset(validPreset);
                this.onChange?.();
            }, 1000);
        };

        // If initialization is already done, apply after delay.
        if (useAppStore.getState().settingsRequestDone) {
            applyAfterDelay();
            return;
        }

        // Otherwise wait for settingsRequestDone to become true.
        this.pendingRestore = true;
        this.storeUnsubscribe = useAppStore.subscribe((state) => {
            if (this.pendingRestore && state.settingsRequestDone) {
                this.pendingRestore = false;
                this.storeUnsubscribe?.();
                this.storeUnsubscribe = null;
                applyAfterDelay();
            }
        });
    }

    destroy(): void {
        this.storeUnsubscribe?.();
        this.storeUnsubscribe = null;
        this.jackpotStoreUnsubscribe?.();
        this.jackpotStoreUnsubscribe = null;
        this.instantMockUnsubscribe?.();
        this.instantMockUnsubscribe = null;
        this.winsMockUnsubscribe?.();
        this.winsMockUnsubscribe = null;
        this.pendingRestore = false;
        this.cancelDebounce();
        this.onChange = null;
    }

    /* ── Snapshot building (export) ────────────────────────────── */

    buildPreset(): QaPreset {
        const boState = this.backoffice.getState();
        const jpState = this.jackpotMock.getState();

        // Capture section layout: section names in order + game keys per section.
        const sectionLayout: QaPresetSectionLayout[] = boState.sections.map((s) => ({
            name: s.name,
            gameKeys: s.games.map((g) => g.gameKey),
        }));

        // Collect game overrides — de-duplicate by gameKey (first occurrence wins).
        const gameOverrides: Record<string, QaPresetGameOverride> = {};
        boState.sections.forEach((s) => {
            s.games.forEach((g) => {
                if (gameOverrides[g.gameKey]) return;
                gameOverrides[g.gameKey] = {
                    topGame: g.topGame,
                    isInTournament: g.isInTournament,
                    gameFeatures: [...g.gameFeatures],
                    jackpotName: g.jackpotName,
                    displayJackpotName: g.displayJackpotName,
                    activatedOn: g.activatedOn,
                    addedToLobbyOn: g.addedToLobbyOn,
                    currentPlayers: g.currentPlayers,
                    minBet: g.minBet,
                    maxBet: g.maxBet,
                };
            });
        });

        // Bet currency from the first game or from appStore.
        let betCurrency = '';
        for (const section of boState.sections) {
            if (section.games.length > 0) {
                betCurrency = section.games[0].currency;
                break;
            }
        }
        if (!betCurrency) {
            betCurrency = useAppStore.getState().balance?.currency ?? '';
        }

        // Jackpot overrides — flatten all levels.
        const jackpotOverrides: QaPreset['jackpotOverrides'] = [];
        jpState.jackpots.forEach((jp) => {
            jp.levels.forEach((lvl) => {
                jackpotOverrides.push({
                    jackpotName: jp.name,
                    levelId: lvl.levelId,
                    value: lvl.value,
                });
            });
        });

        // Campaigns: capture mockMode + every tournament whose id starts with
        // `qa-mock-`. The full ITournament objects carry `games: IGameSettings[]`
        // (Maps inside) once parsed, which doesn't survive JSON.stringify
        // cleanly. We strip them back to `games: string[]` (gameKey list) so
        // the JSON is portable; on import, addMockTournament's parser rebuilds
        // the IGameSettings from appStore.supportedGames.
        const mockTournaments: ITournament[] = (useCampaignsStore.getState().campaigns
            .filter(isTournamentSource) as (ITournament | IParsedTournament)[])
            .filter((t) => t.id.startsWith('qa-mock-'))
            .map((t) => stripParsedGames(t));

        // PrizePop: capture mockMode (on `campaignsStore`, distinct from
        // tournaments' flag) + every PrizePop campaign with a `qa-mock-` id.
        // PrizePop natively uses `string[]` for `games`, so no stripping
        // step is needed — the entries serialize cleanly.
        const campaignsStoreState = useCampaignsStore.getState();
        const mockPrizePopCampaigns: IPrizePopCampaign[] = campaignsStoreState.campaigns
            .filter((c): c is IPrizePopCampaign =>
                'type' in c && c.type === 'prize_pop' && c.id.startsWith('qa-mock-')
            );

        // Rebate: same pattern as PrizePop — capture mockMode + every rebate
        // campaign with a `qa-mock-` id. Rebate natively uses `string[]` for
        // `games`, so no stripping step is needed.
        const mockRebateCampaigns: IRebateCampaign[] = campaignsStoreState.campaigns
            .filter(isRebateSource)
            .filter((c) => c.id.startsWith('qa-mock-'));

        // InstantLink synthetic injection — null unless QA injected one this
        // session; only then does it round-trip through the preset.
        const instantJackpotMock = this.jackpotMock.getInstantMock();

        return {
            version: 1,
            timestamp: new Date().toISOString(),
            sectionLayout,
            gameOverrides,
            betCurrency,
            casino: {
                openType: boState.openType,
                seasonalBranding: boState.seasonalBranding,
                noJackpot: boState.noJackpot,
                currency: boState.currency,
            },
            jackpotOverrides,
            ...(instantJackpotMock ? { instantJackpotMock } : {}),
            jackpotWinsMock: this.jackpotMock.getWinsMock(),
            campaigns: {
                mockMode: campaignsStoreState.mockMode,
                mockTournaments,
                prizePopMockMode: campaignsStoreState.mockMode,
                mockPrizePopCampaigns,
                rebateMockMode: campaignsStoreState.mockMode,
                mockRebateCampaigns,
            },
            theme: safeGetItem('qa-panel-theme') ?? 'dark',
        };
    }

    /* ── Preset application (import) ──────────────────────────── */

    applyPreset(preset: QaPreset): PresetImportResult {
        let applied = 0;
        let skipped = 0;

        try {
            // 1. Casino settings
            useAppStore.getState().updateSettings({ openType: preset.casino.openType });
            if (preset.casino.seasonalBranding) {
                useFeatureFlagsStore.getState().setSeasonalBranding(
                    preset.casino.seasonalBranding as BrandingType
                );
            } else {
                useFeatureFlagsStore.getState().setFlags({
                    ...useFeatureFlagsStore.getState().flags,
                    seasonalBranding: null,
                });
            }
            // noJackpot — older presets may not include this field; only
            // apply when it's explicitly present so legacy imports don't
            // silently strip an existing 'noJackpot' restriction.
            if (typeof preset.casino.noJackpot === 'boolean') {
                const currentRestrictions = useAppStore.getState().settings.restrictions ?? [];
                const filteredRestrictions = currentRestrictions.filter((r) => r !== 'noJackpot');
                const nextRestrictions = preset.casino.noJackpot
                    ? [...filteredRestrictions, 'noJackpot']
                    : filteredRestrictions;
                useAppStore.getState().updateSettings({ restrictions: nextRestrictions });
            }
            // currency — optional in the schema; older presets may omit it.
            // Only apply when present and non-empty so legacy imports don't
            // wipe the loaded balance currency.
            if (typeof preset.casino.currency === 'string' && preset.casino.currency.length > 0) {
                this.backoffice.setCurrency(preset.casino.currency);
            }

            // 2. QA panel theme — persist to localStorage so the next popup
            //    open picks it up. Also included in importResult so the popup
            //    can apply it immediately if already open.
            if (preset.theme) {
                safeSetItem('qa-panel-theme', preset.theme);
            }

            // 3. Restore section layout (order + game membership).
            //    This rebuilds the sections array from the preset's layout
            //    blueprint using processed IGameSettings from the current store
            //    (donor strategy — same as backofficeController.addGameToSection).
            if (preset.sectionLayout && preset.sectionLayout.length > 0) {
                this.applySectionLayout(preset.sectionLayout);
            }

            // 4. Game field overrides (topGame, bets, currentPlayers, etc.).
            //    Runs AFTER section layout so the games are already in place.
            const boState = this.backoffice.getState();
            const knownGameKeys = new Set<string>();
            boState.sections.forEach((s) => {
                s.games.forEach((g) => {
                    knownGameKeys.add(g.gameKey);
                });
            });

            for (const [gameKey, override] of Object.entries(preset.gameOverrides)) {
                if (!knownGameKeys.has(gameKey)) {
                    skipped++;
                    continue;
                }
                this.backoffice.updateGame(gameKey, {
                    topGame: override.topGame,
                    isInTournament: override.isInTournament,
                    gameFeatures: override.gameFeatures,
                    jackpotName: override.jackpotName,
                    displayJackpotName: override.displayJackpotName,
                    activatedOn: override.activatedOn,
                    addedToLobbyOn: override.addedToLobbyOn,
                    currentPlayers: override.currentPlayers,
                    minBet: override.minBet,
                    maxBet: override.maxBet,
                });
                applied++;
            }

            // 5. Jackpot overrides — defer if jackpot data hasn't arrived yet.
            //    setLevelValue auto-enables mock mode (stops polling), so calling
            //    it before data loads means jackpots will NEVER arrive. Wait for
            //    hasData before applying.
            if (preset.jackpotOverrides && preset.jackpotOverrides.length > 0) {
                this.applyJackpotOverrides(preset.jackpotOverrides);
            }

            // 5b. InstantLink synthetic injection. Defensive typeof guards so
            //     older presets (no field) pass through untouched. Injection
            //     also auto-enables mock mode, so it too waits for hasData —
            //     but unlike the per-level overrides it does NOT require
            //     size > 0: some QA setups legitimately have zero jackpots in
            //     the poll response, and injection is exactly the tool for
            //     that case (it creates the instance rather than editing one).
            if (
                preset.instantJackpotMock &&
                typeof preset.instantJackpotMock === 'object' &&
                typeof preset.instantJackpotMock.value === 'number'
            ) {
                this.applyInstantJackpotMock(preset.instantJackpotMock.value);
            }

            // 5c. Statistics-drawer win mocking. Same defensive shape as above so older presets
            //     (no field) leave the live mock state alone.
            this.applyJackpotWinsMock(preset.jackpotWinsMock);

            // 6. Campaigns (tournaments mock state). Defensive type checks so
            //    older presets without this field don't crash or wipe live state.
            //    By the time this runs we've already cleared the
            //    `settingsRequestDone + 1s` gate, so appStore.supportedGames is
            //    populated and addMockTournament's parser can resolve game keys.
            this.applyCampaigns(preset.campaigns);

            return { success: true, applied, skipped };
        } catch (err) {
            return {
                success: false,
                applied,
                skipped,
                error: err instanceof Error ? err.message : String(err),
            };
        }
    }

    /**
     * Rebuild the sections array to match the preset's layout.
     *
     * For each section in the layout:
     *   - Find the matching section in the current store by name
     *   - Rebuild its games array using the layout's gameKey order
     *   - Use the donor strategy: find each game's processed IGameSettings
     *     from any section in the current store (same as addGameToSection)
     *   - Sections in the preset that don't exist in the store are skipped
     *   - Sections in the store that aren't in the preset are dropped
     *     (they were deleted by QA)
     *
     * Pushes the result via setSections in one atomic call.
     */
    private applySectionLayout(layout: QaPresetSectionLayout[]): void {
        const store = useAppStore.getState();
        const currentSections = store.sections;

        // Build a lookup of current sections by name.
        const sectionByName = new Map<string, ISectionSettings>();
        for (const s of currentSections) {
            sectionByName.set(s.name as string, s);
        }

        // Build a global game donor pool: gameKey → processed IGameSettings.
        // A game may appear in multiple sections; any instance works (they
        // carry identical field values — same as the backoffice addGame logic).
        const gameDonors = new Map<string, IGameSettings>();
        for (const s of currentSections) {
            for (const g of s.games || []) {
                if (!gameDonors.has(g.gameKey)) {
                    gameDonors.set(g.gameKey, g);
                }
            }
        }

        // Rebuild sections in the layout's order.
        const rebuilt: ISectionSettings[] = [];
        for (const entry of layout) {
            const original = sectionByName.get(entry.name);
            if (!original) continue; // section doesn't exist in this lobby config

            // Rebuild the games array from the layout's gameKey order.
            const games: IGameSettings[] = [];
            for (const gameKey of entry.gameKeys) {
                const donor = gameDonors.get(gameKey);
                if (donor) {
                    games.push({ ...donor }); // shallow clone — Maps are read-only
                }
                // Games not in the donor pool are silently dropped
                // (they may not exist in this operator's config).
            }

            rebuilt.push({
                ...original,
                games,
            } as ISectionSettings);
        }

        // Push in one atomic setSections call.
        if (rebuilt.length > 0) {
            store.setSections(rebuilt);
        }
    }

    /**
     * Apply jackpot level overrides. If jackpot data hasn't loaded yet
     * (hasData === false), subscribe to the jackpots store and apply
     * once the first data arrives. This avoids the trap where
     * setLevelValue auto-enables mock mode (stopping polling) before
     * any jackpot data exists, which would mean data never arrives.
     */
    private applyJackpotOverrides(
        overrides: QaPreset['jackpotOverrides']
    ): void {
        const jpStore = useJackpotsStore.getState();
        if (jpStore.hasData && jpStore.jackpotsFromAPI.size > 0) {
            this.doApplyJackpotOverrides(overrides);
            return;
        }

        // Jackpots haven't loaded yet — wait for them.
        this.jackpotStoreUnsubscribe?.();
        this.jackpotStoreUnsubscribe = useJackpotsStore.subscribe((state) => {
            if (state.hasData && state.jackpotsFromAPI.size > 0) {
                this.jackpotStoreUnsubscribe?.();
                this.jackpotStoreUnsubscribe = null;
                this.doApplyJackpotOverrides(overrides);
            }
        });
    }

    /**
     * Apply the campaigns slice of the preset (tournaments, PrizePop, and Rebate):
     *   - `mockMode` flag is mirrored onto the store for each sub-tab
     *   - mock tournaments are re-injected via `addMockCampaign` (which
     *     parses `games: string[]` → `IGameSettings[]` against
     *     `appStore.supportedGames`)
     *   - mock PrizePop and Rebate campaigns are re-injected via
     *     `addMockCampaign` (no parsing needed — `games` is natively `string[]`)
     *
     * Defensive type checks let older presets (no `campaigns` field, or
     * missing `prizePopMockMode` / `rebateMockMode`) pass through untouched —
     * live state is not wiped on apply. If a mockMode field exists but is
     * false, we ensure mocking is OFF to match the preset (otherwise an
     * enabled session would persist after a "mocking off" preset gets
     * imported).
     */
    private applyCampaigns(campaigns: QaPreset['campaigns']): void {
        if (!campaigns || typeof campaigns !== 'object') return;

        const wantsMockMode = typeof campaigns.mockMode === 'boolean'
            ? campaigns.mockMode
            : null;
        const mockTournaments = Array.isArray(campaigns.mockTournaments)
            ? campaigns.mockTournaments
            : [];

        if (wantsMockMode === true) {
            this.campaigns.enableMocking();
        } else if (wantsMockMode === false) {
            // Match the preset: turn mocking off and clear any leftover mocks.
            // disableMocking() is the right door — it flips mockMode and
            // clears the qa-mock-* entries (the editing session is over).
            const currentMockMode = useCampaignsStore.getState().mockMode;
            if (currentMockMode) {
                this.campaigns.disableMocking();
            }
        }

        // Inject each mocked tournament. `addMockTournament` is idempotent
        // re: mockMode (it never toggles the flag) and runs the games parser
        // against appStore.supportedGames, so by the time we land here (after
        // the settingsRequestDone + 1s gate) the parsed IGameSettings is
        // rebuilt cleanly.
        for (const t of mockTournaments) {
            if (!t || typeof t !== 'object' || typeof t.id !== 'string') continue;
            if (!t.id.startsWith('qa-mock-')) continue;
            useCampaignsStore.getState().addMockCampaign({ ...t, type: TOURNAMENTS_CAMPAIGN_TYPE } as never);
        }

        // PrizePop slice — independent mockMode flag on `campaignsStore`. The
        // brief carries `prizePopMockMode` / `mockPrizePopCampaigns` as
        // optional fields, so older presets fall through untouched.
        const wantsPpMockMode = typeof campaigns.prizePopMockMode === 'boolean'
            ? campaigns.prizePopMockMode
            : null;
        const mockPrizePopCampaigns = Array.isArray(campaigns.mockPrizePopCampaigns)
            ? campaigns.mockPrizePopCampaigns
            : [];

        if (wantsPpMockMode === true) {
            this.prizePop.enableMocking();
        } else if (wantsPpMockMode === false) {
            const currentPpMockMode = useCampaignsStore.getState().mockMode;
            if (currentPpMockMode) {
                this.prizePop.disableMocking();
            }
        }

        // Inject each mocked PrizePop campaign through the store's append
        // path so consumers see the same ordering as a fresh QA wizard run.
        for (const c of mockPrizePopCampaigns) {
            if (!c || typeof c !== 'object' || typeof c.id !== 'string') continue;
            if (!c.id.startsWith('qa-mock-')) continue;
            useCampaignsStore.getState().addMockCampaign(c);
        }

        // Rebate slice — same pattern as PrizePop. The brief carries
        // `rebateMockMode` / `mockRebateCampaigns` as optional fields, so
        // older presets fall through untouched.
        const wantsRbMockMode = typeof campaigns.rebateMockMode === 'boolean'
            ? campaigns.rebateMockMode
            : null;
        const mockRebateCampaigns = Array.isArray(campaigns.mockRebateCampaigns)
            ? campaigns.mockRebateCampaigns
            : [];

        if (wantsRbMockMode === true) {
            this.rebate.enableMocking();
        } else if (wantsRbMockMode === false) {
            const currentRbMockMode = useCampaignsStore.getState().mockMode;
            if (currentRbMockMode) {
                this.rebate.disableMocking();
            }
        }

        for (const c of mockRebateCampaigns) {
            if (!c || typeof c !== 'object' || typeof c.id !== 'string') continue;
            if (!c.id.startsWith('qa-mock-')) continue;
            // Older presets may lack `cashbackLevels` — fall back to the default 3 levels.
            const cashbackLevels =
                Array.isArray(c.cashbackLevels) && c.cashbackLevels.length > 0
                    ? c.cashbackLevels
                    : buildDefaultCashbackLevels();
            useCampaignsStore.getState().addMockCampaign({ ...c, cashbackLevels, type: REBATE_CAMPAIGN_TYPE } as never);
        }
    }

    /**
     * Apply the InstantLink injection. Waits for `hasData` (the FIRST poll
     * completed) so injection's auto-enabled mock mode can't stop polling
     * before any jackpot data exists — but deliberately does NOT wait for
     * `jackpotsFromAPI.size > 0` the way applyJackpotOverrides does: on QA
     * envs the poll may legitimately return zero jackpots, and injecting a
     * synthetic InstantLink into an empty store is a valid (and primary)
     * use case.
     */
    private applyInstantJackpotMock(value: number): void {
        const jpStore = useJackpotsStore.getState();
        if (jpStore.hasData) {
            this.jackpotMock.injectInstantJackpot(value);
            return;
        }

        this.instantMockUnsubscribe?.();
        this.instantMockUnsubscribe = useJackpotsStore.subscribe((state) => {
            if (state.hasData) {
                this.instantMockUnsubscribe?.();
                this.instantMockUnsubscribe = null;
                this.jackpotMock.injectInstantJackpot(value);
                this.onChange?.();
            }
        });
    }

    /**
     * Apply the statistics-drawer win mocking slice.
     *
     * Defensive throughout so an older preset (field absent) leaves the live mock state alone, and
     * a hand-edited one can't push a malformed entry into the generator.
     *
     * `mockMode: false` explicitly tears the mock down, mirroring how `applyCampaigns` treats
     * `mockMode: false` — otherwise an enabled session would survive importing a "mocking off"
     * preset.
     *
     * Generation is deferred the same way `applyJackpotOverrides` is: `generateWinStats` reads
     * the jackpot's poll entry (level ids, currency, games), so running it before the first poll
     * lands would generate the wrong levels.
     */
    private applyJackpotWinsMock(winsMock: QaPreset['jackpotWinsMock']): void {
        if (!winsMock || typeof winsMock !== 'object') return;

        const entries = Array.isArray(winsMock.entries)
            ? winsMock.entries.filter(
                  (entry): entry is JackpotWinsMockEntry =>
                      !!entry &&
                      typeof entry === 'object' &&
                      typeof entry.jackpotName === 'string' &&
                      entry.jackpotName.length > 0 &&
                      typeof entry.winsPerLevel === 'number' &&
                      typeof entry.includeZeroWinLevel === 'boolean'
              )
            : [];

        if (winsMock.mockMode !== true) {
            // Explicit "off" — clear whatever this session generated. Nothing to defer.
            if (typeof winsMock.mockMode === 'boolean') {
                this.jackpotMock.setWinsMockMode(false);
            }
            return;
        }

        if (entries.length === 0) {
            // Mocking on with nothing generated is a legitimate state (every jackpot reads as
            // "no wins", so no panel level is clickable), and needs no jackpot data to restore.
            this.jackpotMock.setWinsMockMode(true);
            return;
        }

        const jpStore = useJackpotsStore.getState();
        if (jpStore.hasData && jpStore.jackpotsFromAPI.size > 0) {
            this.doApplyJackpotWinsMock(entries);
            return;
        }

        this.winsMockUnsubscribe?.();
        this.winsMockUnsubscribe = useJackpotsStore.subscribe((state) => {
            if (state.hasData && state.jackpotsFromAPI.size > 0) {
                this.winsMockUnsubscribe?.();
                this.winsMockUnsubscribe = null;
                this.doApplyJackpotWinsMock(entries);
            }
        });
    }

    private doApplyJackpotWinsMock(entries: JackpotWinsMockEntry[]): void {
        for (const entry of entries) {
            this.jackpotMock.generateWinStats(entry);
        }
        this.onChange?.();
    }

    private doApplyJackpotOverrides(
        overrides: QaPreset['jackpotOverrides']
    ): void {
        for (const entry of overrides) {
            try {
                this.jackpotMock.setLevelValue(
                    entry.jackpotName,
                    entry.levelId,
                    entry.value
                );
            } catch {
                // eslint-disable-next-line no-console
                console.warn(
                    '[QA Panel] Failed to apply jackpot override for',
                    entry.jackpotName,
                    'level',
                    entry.levelId
                );
            }
        }
        this.onChange?.();
    }

    /* ── Auto-save management ─────────────────────────────────── */

    setAutoSave(enabled: boolean): void {
        this.autoSaveEnabled = enabled;
        if (enabled) {
            safeSetItem(LS_AUTO_SAVE_KEY, '1');
            // If a stored preset exists, apply it immediately.
            const stored = safeGetItem(LS_PRESET_KEY);
            if (stored) {
                try {
                    const parsed: unknown = JSON.parse(stored);
                    if (this.isValidPreset(parsed)) {
                        this.applyPreset(parsed);
                    }
                } catch {
                    // ignore corrupted data
                }
            }
            // Save the current state immediately.
            this.saveToLocalStorage();
        } else {
            safeRemoveItem(LS_AUTO_SAVE_KEY);
            this.cancelDebounce();
        }
        this.onChange?.();
    }

    clearStorage(): void {
        safeRemoveItem(LS_AUTO_SAVE_KEY);
        safeRemoveItem(LS_PRESET_KEY);
        this.autoSaveEnabled = false;
        this.lastSavedAt = null;
        this.cancelDebounce();
        this.onChange?.();
    }

    /**
     * Called by the bridge on every state-affecting change. If auto-save
     * is enabled, debounces a write to localStorage.
     */
    onStateChange(): void {
        if (!this.autoSaveEnabled) return;
        this.cancelDebounce();
        this.debounceTimer = setTimeout(() => {
            this.saveToLocalStorage();
        }, AUTO_SAVE_DEBOUNCE_MS);
    }

    getState(): PresetState {
        return {
            autoSaveEnabled: this.autoSaveEnabled,
            hasStoredPreset: safeGetItem(LS_PRESET_KEY) !== null,
            lastSavedAt: this.lastSavedAt,
        };
    }

    /* ── Internal ─────────────────────────────────────────────── */

    private saveToLocalStorage(): void {
        const preset = this.buildPreset();
        const json = JSON.stringify(preset);
        safeSetItem(LS_PRESET_KEY, json);
        this.lastSavedAt = new Date().toISOString();
        this.onChange?.();
    }

    private cancelDebounce(): void {
        if (this.debounceTimer !== null) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }

    private isValidPreset(data: unknown): data is QaPreset {
        if (typeof data !== 'object' || data === null) return false;
        const obj = data as Record<string, unknown>;
        return obj['version'] === 1 && typeof obj['gameOverrides'] === 'object';
    }
}
